/**
 * "3 dispute · 0 support · 2 context" — how the sources a check kept sided with
 * the claim, as one line, on every reading that kept any.
 *
 * The card's check and the feed's check result print it, and nothing else does:
 * the reading label says what the check came to, and this says what it came to
 * it from, as a count a reader can hold two cards against. "Mixed reading" over
 * three sources that all disputed the claim and one over three that split both
 * wore the same two words; a count tells them apart at a glance, where the note
 * under the label had to be read to the end.
 *
 * Each count carries the rule its rows wear in the source list — the refuted
 * hue for a dispute, the signal hue for support, the hairline for the rest — so
 * the line and the list it counts are visibly one record, and the marks are what
 * set the counts apart. A zero's mark is dimmed rather than dropped: the strip
 * keeps its shape from card to card.
 *
 * The words and the count are core's (`stanceParts` over `stanceTally`), shared
 * with the sentence a screen reader hears when a check lands (`stanceCount`).
 */

import { offTopicSupport } from '../core/judged'
import { stanceTally, type WebSource } from '../core/types'
import { stanceParts } from '../core/vocabulary'

export function StanceStrip({ sources }: { sources: readonly WebSource[] }) {
  const parts = stanceParts(stanceTally(sources))
  if (!parts.length) return null
  const offTopic = offTopicSupport(sources)
  return (
    <span
      className="stance-strip"
      data-stance-strip
      title="How the sources this check kept bear on the claim, as the check labelled each one. A count says that sources disagree, never on what — the summary says on what."
    >
      {parts.map((p, i) => (
        <span key={p.stance} data-stance={p.stance} data-none={p.n === 0 || undefined}>
          {/* The marks set the counts apart on screen; the separator is the
              text form's (`stanceStrip`), so a screen reader and a copied line
              read "3 dispute · 0 support" and not one run of numbers. */}
          {i > 0 && <span className="sr-only"> · </span>}
          <i aria-hidden />
          {p.text}
        </span>
      ))}
      {/* The judge's one strong call, beside the count it qualifies and never in
          place of it: the support count stays the check's own (D378). */}
      {offTopic && (
        <span
          data-judged-off-topic
          title={`${offTopic.model}, a second reader, read ${offTopic.n === 1 ? 'one quote' : `${offTopic.n} quotes`} this check counted as support as not bearing on the claim, at a probability of 0.85 or more. The counts and the reading above are the check's own; this changes neither.`}
        >
          <span className="sr-only"> · </span>
          {offTopic.model}: {offTopic.n} off-topic
        </span>
      )}
    </span>
  )
}
